import { RouteProps } from 'react-router';
import { Navigate } from 'react-router-dom';
import { EnrollmentsView } from '@/views/EnrollmentsView';
import { EnrollmentIntakeView } from '@/views/EnrollmentIntakeView';
import { EnrollmentTermsView } from '@/views/EnrollmentTermsView';
import { EnrollmentLessonView } from '@/views/EnrollmentLessonView';
import { AssignmentDetailsView } from '@/views/AssignmentDetailsView';
import { AssignmentsView } from '@/views/AssignmentsView';
import {
  CreateRequestView,
  ProfileView,
  RequestDetailView,
  TeacherCreateRequestView,
  TeacherRequestDetailView,
} from '@/views/ProfileView';
import { CertificateView } from '@/views/CertificateView';
import { TeacherAssignmentsView } from '@/views/TeacherAssignmentsView';
import { AccountType } from '@/entities/Profile';
import { routerPath } from '@/shared/config/router';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { AssignmentsPage } from '@/pages/AssignmentsPage';
import { CataloguePage } from '@/pages/CataloguePage';
import { CalendarPage } from '@/pages/CalendarPage';
import { AcademicStaffPage } from '@/pages/AcademicStaffPage';
import { CourseDetailPage } from '@/pages/CourseDetailPage';
import { EnrollmentsPage } from '@/pages/EnrollmentsPage';
import { ConfirmEmailPage } from '@/pages/ConfirmEmailPage';
import { SetPasswordPage } from '@/pages/SetPasswordPage';
import { ActivatePage } from '@/pages/ActivatePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { TeacherAssignmentsPage } from '@/pages/TeacherAssignmentsPage';
import { TeacherCoursesPage } from '@/pages/TeacherCoursesPage';
import TeacherCourseDetailPage from '@/pages/TeacherCourseDetailPage';
import { TeacherAssignmentDetailsPage } from '@/pages/TeacherAssignmentDetailsPage';
import { TeacherAssessmentPage } from '@/pages/TeacherAssessmentPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { TeacherModuleLessonsPage } from '@/pages/TeacherModuleLessonsPage';
import { TeacherLessonPage } from '@/pages/TeacherLessonPage';
import GroupPage from '@/pages/GroupPage';
import TermDetailPage from '@/pages/TermDetailPage';
import TeacherModuleAttendancePage from '@/pages/TeacherModuleAttendancePage';
import { ChatsPage } from '@/pages/ChatsPage';
import { PushNotificationsPage } from '@/pages/PushNotificationsPage';
import { BlogDetailPage } from '@/pages/BlogDetailPage';

export type TypeRouteConfig = RouteProps & {
  isAuth?: boolean;
  allowAuthenticated?: boolean;
  allowedAccountTypes?: AccountType[];
  nested?: TypeRouteConfig[];
};

export const routeConfig: TypeRouteConfig[] = [
  {
    path: routerPath.main,
    element: <HomePage />,
    isAuth: true,
    // Общий роут для всех ролей
  },
  {
    path: routerPath.login,
    element: <LoginPage />,
  },
  {
    path: routerPath.confirmEmail,
    element: <ConfirmEmailPage />,
  },
  {
    path: routerPath.setPassword,
    element: <SetPasswordPage />,
  },
  {
    path: routerPath.activate,
    element: <ActivatePage />,
    allowAuthenticated: true,
  },
  {
    path: routerPath.forgotPassword,
    element: <ForgotPasswordPage />,
  },
  {
    path: routerPath.resetPassword,
    element: <ResetPasswordPage />,
  },
  {
    path: routerPath.chats,
    element: <ChatsPage />,
    isAuth: true,
  },
  {
    path: routerPath.pushNotifications,
    element: <PushNotificationsPage />,
    isAuth: true,
  },
  {
    path: routerPath.enrollments,
    element: <EnrollmentsPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Student],
    nested: [
      {
        index: true,
        element: <EnrollmentsView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.enrollment,
        element: <EnrollmentIntakeView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
        nested: [
          {
            path: routerPath.enrollment_terms,
            element: <EnrollmentTermsView />,
            isAuth: true,
            allowedAccountTypes: [AccountType.Student],
          },
        ],
      },
      {
        path: `${routerPath.enrollment}/${routerPath.enrollment_group_lesson}`,
        element: <EnrollmentLessonView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: `${routerPath.enrollment}/${routerPath.enrollment_group_exam_resubmission}`,
        element: <EnrollmentLessonView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
    ],
  },
  {
    path: routerPath.assignments,
    element: <AssignmentsPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Student],
    nested: [
      {
        index: true,
        element: <AssignmentsView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.assignmentGroupLesson,
        element: <AssignmentDetailsView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.assignmentExamResubmission,
        element: <AssignmentDetailsView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
    ],
  },
  {
    path: routerPath.catalogue,
    element: <CataloguePage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Student],
  },
  {
    path: routerPath.course,
    element: <CourseDetailPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Student],
  },
  {
    path: routerPath.calendar,
    element: <CalendarPage />,
    isAuth: true,
    // Общий роут для всех ролей
  },
  {
    path: routerPath.blog,
    element: <BlogDetailPage />,
    isAuth: true,
  },
  {
    path: routerPath.profile,
    element: <ProfilePage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Student, AccountType.Staff],
    nested: [
      {
        index: true,
        element: <ProfileView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student, AccountType.Staff],
      },
      {
        path: routerPath.certificate,
        element: <CertificateView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.profileRequestCreate,
        element: <CreateRequestView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.profileAttendanceRequestCreate,
        element: <CreateRequestView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.profileRequestDetail,
        element: <RequestDetailView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Student],
      },
      {
        path: routerPath.teacherProfileRequestCreate,
        element: <TeacherCreateRequestView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
      {
        path: routerPath.teacherProfileRequestDetail,
        element: <TeacherRequestDetailView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
    ],
  },
  {
    path: routerPath.settings,
    element: <SettingsPage />,
    isAuth: true,
    // Общий роут для всех ролей
  },
  {
    path: routerPath.staffProfile,
    element: <AcademicStaffPage />,
    isAuth: true,
    // Общий роут для всех ролей
  },
  // Teacher Assignments flow: /teacher/assignments and nested (group-lesson, assessment, etc.)
  // Independent from teacher/courses — separate routes, APIs, and params
  {
    path: routerPath.teacherAssignments,
    element: <TeacherAssignmentsPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
    nested: [
      {
        index: true,
        element: <TeacherAssignmentsView />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
      {
        path: routerPath.teacherAssignmentDetailsGroupLesson,
        element: <TeacherAssignmentDetailsPage />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
      {
        path: routerPath.teacherAssignmentDetailsGroupExamResubmission,
        element: <TeacherAssignmentDetailsPage />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
      {
        path: routerPath.teacherGroupLessonAssessment,
        element: <TeacherAssessmentPage />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
      {
        path: routerPath.teacherGroupExamResubmissionAssessment,
        element: <TeacherAssessmentPage />,
        isAuth: true,
        allowedAccountTypes: [AccountType.Staff],
      },
    ],
  },
  // Teacher Courses flow: /teacher/courses, course detail, group, term
  // Independent from teacher/assignments — separate routes, APIs, and params
  {
    path: routerPath.teacherCourses,
    element: <TeacherCoursesPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherCourse,
    element: <TeacherCourseDetailPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherCourseGroup,
    element: <GroupPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherCourseGroupTerm,
    element: <TermDetailPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  // Nested under teacher/courses: module lessons, attendance, lesson (course flow only)
  {
    path: routerPath.teacherCourseGroupTermModuleLessons,
    element: <TeacherModuleLessonsPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherCourseGroupTermModuleAttendance,
    element: <TeacherModuleAttendancePage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherCourseGroupTermModuleLesson,
    element: <TeacherLessonPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  // Standalone module/lesson routes (used from teacher/assignments flow — do not remove)
  {
    path: routerPath.teacherModuleLessons,
    element: <TeacherModuleLessonsPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherModuleAttendance,
    element: <TeacherModuleAttendancePage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.teacherLesson,
    element: <TeacherLessonPage />,
    isAuth: true,
    allowedAccountTypes: [AccountType.Staff],
  },
  {
    path: routerPath.not_found,
    element: <Navigate to={routerPath.main} replace />,
    isAuth: true,
  },
];
